import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';

import { fetchLoggedInCustomer } from 'flow/actions/customers-actions';
import { removeProfile } from 'flow/actions/profile-actions';
import SettingsListItem from 'flow/components/custom-components/SettingsListItem';
import PbxMenuIcon from 'flow/components/pbx/PbxMenuIcon';
import Button from 'flow/components/ui-components/Button';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import Loading from 'flow/components/ui-components/Loading';
import RouteLink from 'flow/components/ui-components/RouteLink';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useChannel from 'flow/hooks/useChannel';
import useCustomer from 'flow/hooks/useCustomer';
import useExtension from 'flow/hooks/useExtension';
import useProfiles from 'flow/hooks/useProfiles';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectExtensionIsMemberOfSomeQueue } from 'flow/selectors/queue-selectors';
import { ExtensionKey, ProfileKey } from 'flow/types/EntityKeys';
import { LocationType } from 'flow/types/Location';
import { PlusID } from 'flow/types/Profile';
import { routes } from 'flow/types/routes';
import { extractName, extractStatusFromProfile } from 'flow/utils';
import { getCallRecordingTitle } from 'flow/utils/call-recording-monitoring-utils';
import { isFlowFreeUser } from 'flow/utils/flow-utils';
import { getQueueChannelDescription } from 'flow/utils/pbx-utils';
import {
    getDefaultDurationTextFromId,
    resolveDefaultDuration,
} from 'flow/utils/profile-utils';
import { generatePath } from 'flow/utils/router-utils';
import Page from './Page';
import SettingSection from './SettingSection';

const ProfilePage = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory<LocationType>();
    const { openModal } = useModal();
    const { extensionKey, profileKey } = useParams<{
        extensionKey: ExtensionKey;
        profileKey: ProfileKey;
    }>();

    const { channels } = useChannel(extensionKey);
    const { ownCustomer } = useCustomer(extensionKey);
    const { extension } = useExtension(extensionKey);
    const { getProfile } = useProfiles(extensionKey);
    const isAiCoworker = extension?.get('extensionType') === 'ai_coworker';
    const extensionIsMemberOfSomeQueue = useAppSelector((state) =>
        selectExtensionIsMemberOfSomeQueue(state, extensionKey),
    );

    // Set as any because of mock further down
    const profile: any = getProfile(profileKey);

    useEffect(() => {
        dispatch(fetchLoggedInCustomer(extensionKey));
    }, [dispatch, extensionKey]);

    if (!ownCustomer || !extension || !profile) {
        return <Loading signalFinishedLoading={true} />;
    }
    const isFlowFree = isFlowFreeUser(ownCustomer, extension);
    const showOption = !isFlowFree || !isAiCoworker;

    const goBack = () =>
        history.push(
            history.location.state?.from
                ? history.location.state.from
                : generatePath(routes['settings.profiles'], {
                      extensionKey,
                  }),
        );

    const handleRemove = () => {
        dispatch(
            removeProfile(extensionKey, profileKey, profile.get('description')),
        );

        goBack();
    };

    const contact = extension.get('contact');
    const profileDuration = resolveDefaultDuration(
        profile.get('defaultDuration'),
        moment(),
    );

    /**
     * Creating mockProfileWithActiveUntil to be able to use utils method
     * to get the profile status with duration.
     */
    const mockProfileWithActiveUntil = profileDuration
        ? profile.set('activeUntil', profileDuration)
        : profile;

    const profileCanAccessCallRecording =
        profile.has('recordCalls') ||
        profile.has('autoTranscribe') ||
        profile.has('personalRecordCalls') ||
        profile.has('personalAutoTranscribe') ||
        profile.has('canBeCoMonitored');

    return (
        <Page header={profile?.get('description')} onClickBack={goBack}>
            <SettingSection className="text-sm/4">
                {i18next.t('settings.profile.displayed')}
            </SettingSection>
            <div className="mb-3 flex bg-gray-100 p-6">
                <div className="badge-bg shadow-base flex w-full max-w-sm items-center justify-center gap-x-3 rounded-sm bg-white p-2">
                    <ContactAvatar
                        contact={contact}
                        status={
                            profile.get('available')
                                ? 'available'
                                : 'unavailable'
                        }
                        contactType={extension?.get('extensionType')}
                    />
                    <div className="flex w-full flex-col overflow-hidden whitespace-nowrap">
                        <div className="m-auto flex w-full flex-row justify-between">
                            <div className="overflow-hidden leading-5 text-ellipsis">
                                {extractName(contact)}
                            </div>
                        </div>
                        <div className="text-sm/4 text-gray-500">
                            {extractStatusFromProfile(
                                mockProfileWithActiveUntil,
                                i18next,
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <SettingsListItem
                editable={
                    profile.get('editable') &&
                    ![PlusID.AVAILABLE, PlusID.LEFT_FOR_THE_DAY].includes(
                        profile.get('plusId'),
                    )
                }
                description={profile.get('description')}
                to={generatePath(routes['settings.profiles.profileKey.name'], {
                    extensionKey,
                    profileKey,
                })}
            >
                {i18next.t('pbx.name')}
            </SettingsListItem>
            <SettingsListItem
                editable={
                    profile.get('editable') &&
                    profile.get('plusId') !== PlusID.AVAILABLE
                }
                description={
                    profile.get('available')
                        ? i18next.t('status.available')
                        : i18next.t('status.unavailable')
                }
                to={generatePath(
                    routes['settings.profiles.profileKey.availability'],
                    { extensionKey, profileKey },
                )}
            >
                {i18next.t('status.status')}
            </SettingsListItem>

            {!isAiCoworker && (
                <SettingsListItem
                    editable={profile.get('editable')}
                    description={
                        profile.get('operatorNote') ||
                        i18next.t('settings.profile.no_message')
                    }
                    to={generatePath(
                        routes['settings.profiles.profileKey.note'],
                        {
                            extensionKey,
                            profileKey,
                        },
                    )}
                >
                    {i18next.t('profilechooser.message')}
                </SettingsListItem>
            )}
            <SettingSection className="mt-6 text-sm/4">
                {i18next.t('settings.profile.profile_features')}
            </SettingSection>
            {!isAiCoworker && (
                <RouteLink
                    to={generatePath(
                        routes['settings.profiles.profileKey.notifications'],
                        { extensionKey, profileKey },
                    )}
                >
                    <PbxMenuIcon iconKey="notifications" />

                    {i18next.t('settings.notifications')}
                </RouteLink>
            )}
            {showOption && (
                <RouteLink
                    to={generatePath(
                        routes['settings.profiles.profileKey.callerId'],
                        { extensionKey, profileKey },
                    )}
                >
                    <PbxMenuIcon iconKey="callerid" />

                    {i18next.t('settings.callerid')}
                </RouteLink>
            )}
            {showOption && (
                <RouteLink
                    to={generatePath(
                        routes['settings.profiles.profileKey.incoming-calls'],
                        { extensionKey, profileKey },
                    )}
                >
                    <PbxMenuIcon iconKey="incoming" />

                    {i18next.t('callcontrol.incoming_call')}
                </RouteLink>
            )}
            {showOption && profileCanAccessCallRecording && (
                <RouteLink
                    to={generatePath(
                        routes[
                            'settings.profiles.profileKey.call-recording-and-monitoring'
                        ],
                        { extensionKey, profileKey },
                    )}
                >
                    <PbxMenuIcon iconKey="voicemailBox" />

                    {i18next.t(getCallRecordingTitle(profile))}
                </RouteLink>
            )}
            {extensionIsMemberOfSomeQueue || !channels.isEmpty() ? (
                <RouteLink
                    to={generatePath(
                        routes[
                            'settings.profiles.profileKey.queue-and-chat-login'
                        ],
                        { extensionKey, profileKey },
                    )}
                >
                    <PbxMenuIcon iconKey="queuelogin" />

                    {
                        // @ts-expect-error
                        getQueueChannelDescription([profile], i18next)
                    }
                </RouteLink>
            ) : null}
            <SettingSection className="mt-6 text-sm/4">
                {i18next.t('Queue.Settings.OtherSettings')}
            </SettingSection>
            <SettingsListItem
                editable={profile.get('plusId') !== PlusID.AVAILABLE}
                to={generatePath(
                    routes['settings.profiles.profileKey.duration'],
                    {
                        extensionKey,
                        profileKey,
                    },
                )}
                description={getDefaultDurationTextFromId(profile, i18next)}
            >
                {i18next.t('settings.profile.default_duration')}
            </SettingsListItem>
            {profile.get('editable') &&
                ![
                    PlusID.AVAILABLE,
                    PlusID.LUNCH,
                    PlusID.MEETING,
                    PlusID.LEFT_FOR_THE_DAY,
                ].includes(profile.get('plusId')) && (
                    <div className="mt-6 flex justify-end">
                        <Button
                            color="danger"
                            onClick={() =>
                                openModal('removeDialog', {
                                    title: i18next.t(
                                        'Settings.Profile.Dialog.Tile',
                                    ),
                                    description: i18next.t(
                                        'Settings.Profile.Dialog.Body',
                                        {
                                            profileDescription:
                                                profile.get('description'),
                                        },
                                    ),
                                    onRemove: handleRemove,
                                })
                            }
                        >
                            {i18next.t('remove')}
                        </Button>
                    </div>
                )}
        </Page>
    );
};

export default ProfilePage;
